import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';
export class JobService {
    /**
     * Subscribe Job Notifications
     * SSE provider endpoint for a job.
     *
     * Provides an EventSourceResponse stream with the SSEGenerator for the user
     * specified with the Depends dependency tree.
     * @param jobId
     * @param includeDeleted
     * @returns any Successful Response
     * @throws ApiError
     */
    static subscribeJobNotifications(jobId, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/job/{job_id}/subscribe',
            path: {
                'job_id': jobId,
            },
            query: {
                'include_deleted': includeDeleted,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Get All Jobs
     * Get the full list of all jobs with pagination support.
     *
     * Args:
     * jobs_response (tuple[list[Job], int]): Response from get_jobs_for_user,
     * contains 'jobs' and 'total_count'. Injected by FastAPI.
     *
     * repo (Repository, optional): Repository instance. Injected by FastAPI.
     *
     * Returns:
     * models.FullJobsList: Jobs for the current page and total count.
     * @param skip Jobs to skip
     * @param limit Max jobs
     * @param searchText Text to search in job name and tags
     * @param authors List of author usernames to filter by
     * @param runnableOnly Filter for jobs ready to run
     * @param includeDeleted
     * @returns FullJobsList Successful Response
     * @throws ApiError
     */
    static getAllJobs(skip, limit = 10, searchText, authors, runnableOnly, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/job',
            query: {
                'skip': skip,
                'limit': limit,
                'search_text': searchText,
                'authors': authors,
                'runnable_only': runnableOnly,
                'include_deleted': includeDeleted,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Create Job
     * @param requestBody
     * @returns Job Successful Response
     * @throws ApiError
     */
    static createJob(requestBody) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/job',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Rerun Job
     * Rerun job by using its starting node to restart the code
     * synthesis **with new configuration**. If the job is currently being planned,
     * then it stops planning before rerunning it.
     *
     * This will **not** create a new job.
     * @param jobId
     * @param includeDeleted
     * @param requestBody
     * @returns Job Successful Response
     * @throws ApiError
     */
    static rerunJob(jobId, includeDeleted = false, requestBody) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/job/{job_id}/rerun',
            path: {
                'job_id': jobId,
            },
            query: {
                'include_deleted': includeDeleted,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Retrieve Job
     * Pull in a job from the database in a flattened form.
     *
     * If version_id is provided, returns the job at that specific version.
     * @param jobId
     * @param versionId
     * @param includeDeleted
     * @returns FlatJob Successful Response
     * @throws ApiError
     */
    static retrieveJob(jobId, versionId, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/job/{job_id}',
            path: {
                'job_id': jobId,
            },
            query: {
                'version_id': versionId,
                'include_deleted': includeDeleted,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Delete Job
     * @param jobId
     * @param includeDeleted
     * @returns any Successful Response
     * @throws ApiError
     */
    static deleteJob(jobId, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/job/{job_id}',
            path: {
                'job_id': jobId,
            },
            query: {
                'include_deleted': includeDeleted,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Update Job
     * Update user-specified information for the job.
     *
     * The `JobUpdateData` provided will be used to update the database model,
     * if one exists.
     *
     * Returns the full job object on success.
     * @param jobId
     * @param requestBody
     * @param includeDeleted
     * @returns FlatJob Successful Response
     * @throws ApiError
     */
    static updateJob(jobId, requestBody, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/job/{job_id}',
            path: {
                'job_id': jobId,
            },
            query: {
                'include_deleted': includeDeleted,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Retrieve Job Plan State
     * Get a `.zip` of a job's latest fragment plan state from the database.
     * @param jobId
     * @param includeDeleted
     * @param accept
     * @returns binary Successful Response
     * @throws ApiError
     */
    static retrieveJobPlanState(jobId, includeDeleted = false, accept = 'application/zip') {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/job/{job_id}/plan_state.zip',
            path: {
                'job_id': jobId,
            },
            headers: {
                '-accept': accept,
            },
            query: {
                'include_deleted': includeDeleted,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Set Job Version
     * Sets the version on a job. Will reset all questions in the job tree to be
     * unanswered.
     * @param versionId
     * @param jobId
     * @param includeDeleted
     * @returns any Successful Response
     * @throws ApiError
     */
    static setJobVersion(versionId, jobId, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/job/{job_id}/set_version/{version_id}',
            path: {
                'version_id': versionId,
                'job_id': jobId,
            },
            query: {
                'include_deleted': includeDeleted,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Retrieve Job Lite
     * Pull in a job from the database in a flattened form.
     * @param jobId
     * @param versionId
     * @param includeDeleted
     * @returns JobLite Successful Response
     * @throws ApiError
     */
    static retrieveJobLite(jobId, versionId, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/job/{job_id}/lite',
            path: {
                'job_id': jobId,
            },
            query: {
                'version_id': versionId,
                'include_deleted': includeDeleted,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Retrieve Flat Job
     * Deprecated. Use retrieve_job instead.
     * @param jobId
     * @param includeDeleted
     * @returns FlatJob Successful Response
     * @throws ApiError
     */
    static retrieveFlatJob(jobId, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/job/flat/{job_id}',
            path: {
                'job_id': jobId,
            },
            query: {
                'include_deleted': includeDeleted,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Retrieve Flat Job With Questions
     * Used for job scraping to retrieve the job and all job nodes with questions
     * from previous versions.
     * @param jobId
     * @param includeDeleted
     * @returns FlatJob Successful Response
     * @throws ApiError
     */
    static retrieveFlatJobWithQuestions(jobId, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/job/flat_with_questions/{job_id}',
            path: {
                'job_id': jobId,
            },
            query: {
                'include_deleted': includeDeleted,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Reinitialize Job
     * Reinitialize a job by using its starting node to restart the code
     * synthesis.
     *
     * This will create a new job.
     * @param jobId
     * @param includeDeleted
     * @returns Job Successful Response
     * @throws ApiError
     */
    static reinitializeJob(jobId, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/job/{job_id}/reinitialize',
            path: {
                'job_id': jobId,
            },
            query: {
                'include_deleted': includeDeleted,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Stop Job
     * @param jobId
     * @param includeDeleted
     * @returns any Successful Response
     * @throws ApiError
     */
    static stopJob(jobId, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/job/{job_id}/stop',
            path: {
                'job_id': jobId,
            },
            query: {
                'include_deleted': includeDeleted,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Duplicate Job
     * Duplicate a job in its entirety.
     *
     * This will not restart anything for the orchestrator, just copy the existing
     * job structure into an entirely new object.
     * @param jobId
     * @param includeDeleted
     * @returns Job Successful Response
     * @throws ApiError
     */
    static duplicateJob(jobId, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/job/{job_id}/duplicate',
            path: {
                'job_id': jobId,
            },
            query: {
                'include_deleted': includeDeleted,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Start Job Chat
     * Start a chat for this job.
     * @param jobId
     * @param includeDeleted
     * @returns any Successful Response
     * @throws ApiError
     */
    static startJobChat(jobId, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/job/{job_id}/chat',
            path: {
                'job_id': jobId,
            },
            query: {
                'include_deleted': includeDeleted,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Replace Node
     * Replace the node's text with the provided object.
     * @param jobId
     * @param nodeId
     * @param requestBody
     * @param includeDeleted
     * @returns JobNode_JobInput_ Successful Response
     * @throws ApiError
     */
    static replaceNode(jobId, nodeId, requestBody, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/job/{job_id}/node/{node_id}/replace',
            path: {
                'job_id': jobId,
                'node_id': nodeId,
            },
            query: {
                'include_deleted': includeDeleted,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Select Sugaring
     * Modify the sugaring used for the node.
     *
     * Node must be a question or job step.
     * @param nodeId
     * @param jobId
     * @param requestBody
     * @param includeDeleted
     * @returns any Successful Response
     * @throws ApiError
     */
    static selectSugaring(nodeId, jobId, requestBody, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/job/{job_id}/node/{node_id}/sugaring',
            path: {
                'node_id': nodeId,
                'job_id': jobId,
            },
            query: {
                'include_deleted': includeDeleted,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Expand Node With Context
     * Expand a node with the provided context string.
     * @param jobId
     * @param requestBody
     * @param includeDeleted
     * @returns any Successful Response
     * @throws ApiError
     */
    static expandNodeWithContext(jobId, requestBody, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/job/{job_id}/expand_with_context',
            path: {
                'job_id': jobId,
            },
            query: {
                'include_deleted': includeDeleted,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Update Node
     * Wholesale update a node with the new object provided.
     *
     * Args:
     * node_id (str): Node id to update.
     * node (JobNode): Full JobNode object to update.
     * job (Job): Job a user has been authed for matching the query params.
     * repo (Repository): Repository dependency.
     * @param nodeId
     * @param jobId
     * @param requestBody
     * @param includeDeleted
     * @returns JobNode Successful Response
     * @throws ApiError
     */
    static updateNode(nodeId, jobId, requestBody, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/job/{job_id}/node/{node_id}',
            path: {
                'node_id': nodeId,
                'job_id': jobId,
            },
            query: {
                'include_deleted': includeDeleted,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Delete All Supervised Action Nodes
     * Deletes are pending supervised action comments associated with the job.
     * This should be called when stopping a job.
     * @param jobId
     * @param includeDeleted
     * @returns any Successful Response
     * @throws ApiError
     */
    static deleteAllSupervisedActionNodes(jobId, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/job/{job_id}/delete_all_supervised_actions',
            path: {
                'job_id': jobId,
            },
            query: {
                'include_deleted': includeDeleted,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Delete Comment Node
     * Deletes a comment from the job without interacting with the orchestrator.
     * This is primarily used for supervised-action comments, which do not modify
     * the plan-state, but only interacts with a deployment.
     * @param jobId
     * @param requestBody
     * @param includeDeleted
     * @returns any Successful Response
     * @throws ApiError
     */
    static deleteCommentNode(jobId, requestBody, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/job/{job_id}/delete_comment',
            path: {
                'job_id': jobId,
            },
            query: {
                'include_deleted': includeDeleted,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Add Comment Node
     * Add new comment to the job.
     * @param jobId
     * @param requestBody
     * @param version
     * @param includeDeleted
     * @returns JobNode_JobCommentThread_ Successful Response
     * @throws ApiError
     */
    static addCommentNode(jobId, requestBody, version, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/job/{job_id}/comment',
            path: {
                'job_id': jobId,
            },
            query: {
                'version': version,
                'include_deleted': includeDeleted,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Add Plan State
     * Sets a plan state on a job. Only should be used when creating a job that
     * is resuming an existing plan state. This will set the given plan state as
     * the most recent plan state on the job, its important to make sure the job
     * nodes for this job correspond to this plan state for any future inputs to
     * make sense.
     *
     * Input:
     * plan_state (PlanState): PlanState to set on the job.
     *
     * Dependencies:
     * job (Job): Job a user has been authed for matching the query params.
     * repo (Repository): Repo dependency.
     * @param jobId
     * @param requestBody
     * @param includeDeleted
     * @returns any Successful Response
     * @throws ApiError
     */
    static addPlanState(jobId, requestBody, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/job/{job_id}/plan_state',
            path: {
                'job_id': jobId,
            },
            query: {
                'include_deleted': includeDeleted,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Refresh Job State
     * Requests that the latest plan state associated to this job be sent to the
     * orchestrator so that the job nodes for this job can be re-computed and set
     * on the job.
     *
     * Dependencies:
     * job (Job): Job a user has been authed for matching the query params.
     * repo (Repository): Repo dependency.
     * background_tasks (BackgroundTasks): FastAPI Background Task
     * registrar.
     * @param jobId
     * @param includeDeleted
     * @returns any Successful Response
     * @throws ApiError
     */
    static refreshJobState(jobId, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/job/{job_id}/refresh_job',
            path: {
                'job_id': jobId,
            },
            query: {
                'include_deleted': includeDeleted,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Resume Job State
     * Requests that the latest plan state associated to this job be sent to the
     * orchestrator for planning to be resumed.
     *
     * Dependencies:
     * job (Job): Job a user has been authed for matching the query params.
     * repo (Repository): Repo dependency.
     * background_tasks (BackgroundTasks): FastAPI Background Task
     * registrar.
     * @param jobId
     * @param includeDeleted
     * @returns any Successful Response
     * @throws ApiError
     */
    static resumeJobState(jobId, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/job/{job_id}/resume_job',
            path: {
                'job_id': jobId,
            },
            query: {
                'include_deleted': includeDeleted,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Add Node No Queue
     * Add new input into the job, update edges, but don't send it to the
     * orchestrator.
     *
     * Input:
     * Request Body (CreateJobNode): Node to add to the job
     *
     * Dependencies:
     * job (Job): Job a user has been authed for matching the query params.
     * user (User): Authenticated user model.
     * repo (Repository): Repo dependency.
     * @param jobId
     * @param requestBody
     * @param includeDeleted
     * @returns JobNode_JobInput_ Successful Response
     * @throws ApiError
     */
    static addNodeNoQueue(jobId, requestBody, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/job/{job_id}/node_no_queue',
            path: {
                'job_id': jobId,
            },
            query: {
                'include_deleted': includeDeleted,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Add Node
     * Add new input into the job, update edges, and send work data. This will
     * send the node to the orchestrator for processing.
     *
     * Input:
     * Request Body (CreateJobNode): Node to add to the job
     *
     * Dependencies:
     * job (Job): Job a user has been authed for matching the query params.
     * user (User): Authenticated user model.
     * repo (Repository): Repo dependency.
     * planner_endpoint (WebSocketClientProtocol): Planner connection pipe.
     * background_tasks (BackgroundTasks): FastAPI Background Task registrar.
     * @param jobId
     * @param requestBody
     * @param includeDeleted
     * @returns any Successful Response
     * @throws ApiError
     */
    static addNode(jobId, requestBody, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/job/{job_id}/node',
            path: {
                'job_id': jobId,
            },
            query: {
                'include_deleted': includeDeleted,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Process Job Comments
     * Start processing the batch of inputs on the current version of the job.
     * @param jobId
     * @param includeDeleted
     * @returns any Successful Response
     * @throws ApiError
     */
    static processJobComments(jobId, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/job/{job_id}/process',
            path: {
                'job_id': jobId,
            },
            query: {
                'include_deleted': includeDeleted,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Set Action Supervised
     * Registers or deregisters the given action for supervision. If a deployment
     * is already running, it will be notified of the change.
     *
     * Args:
     * id_in_job (str): The ID of the action in the job.
     *
     * job (models.Job, optional): The ID of the job.
     *
     * version_id (str | None, optional): The optional version of the job.
     * Defaults to None.
     *
     * supervised (bool, optional): Determines whether supervision requests
     * should be generated for this action in the future. Defaults to True.
     *
     * repo (Repository, optional): The MongoDB repository where supervision
     * registration state will be stored.
     *
     * app_settings (AppSettings, optional): The application settings.
     *
     * broadcaster (Broadcast, optional): The broadcaster used to communicate
     * the request to the deployment's appserver.
     *
     * redis (aioredis.Redis, optional): The Redis connection used to
     * communicate with the orchestrator.
     * @param jobId
     * @param idInJob
     * @param versionId
     * @param supervised
     * @param includeDeleted
     * @returns any Successful Response
     * @throws ApiError
     */
    static setActionSupervised(jobId, idInJob, versionId, supervised = true, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/job/{job_id}/supervise',
            path: {
                'job_id': jobId,
            },
            query: {
                'id_in_job': idInJob,
                'version_id': versionId,
                'supervised': supervised,
                'include_deleted': includeDeleted,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Set Job Owners
     * Set the owners for the requested job.
     *
     * This will override the current owners for the job.
     * @param jobId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    static setJobOwners(jobId, requestBody) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/job/{job_id}/owners',
            path: {
                'job_id': jobId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Prepare Job
     * Prepare a job for running by marking it ready_to_run and notifying clients.
     * @param jobId
     * @param versionId
     * @param includeDeleted
     * @returns any Successful Response
     * @throws ApiError
     */
    static prepareJob(jobId, versionId, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/job/{job_id}/prepare',
            path: {
                'job_id': jobId,
            },
            query: {
                'version_id': versionId,
                'include_deleted': includeDeleted,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
}
